import React, { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { usersManagementServices } from '../../APIServices/usersManagementServices';

function UserProfileSettings() {
  const { currentUserData, getCurrentUserDetails } = useData();
  const toast = useToast();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const loadCountries = async () => {
    const data = await usersManagementServices.getCountries();
    setCountryList(data || []);
  };

  const loadStates = async (countryName) => {
    if (!countryName) {
      setStateList([]);
      setCityList([]);
      return;
    }
    const data = await usersManagementServices.getStates(countryName);
    setStateList(data || []);
  };

  const loadCities = async (stateName) => {
    if (!stateName) {
      setCityList([]);
      return;
    }
    const data = await usersManagementServices.getCities(stateName);
    setCityList(data || []);
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast('Please enter your first and last name', 'warn');
      return;
    }

    setButtonLoader(true);

    const payload = {
      userId: currentUserData?.userId,
      status: currentUserData?.status || '',
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      country,
      state,
      city,
      zipCode: zipCode.trim(),
      address1: address1.trim(),
      address2: address2.trim(),
    };

    try {
      const data = await usersManagementServices.updateUserData(payload);
      if (data?.statusCode === 200) {
        await getCurrentUserDetails();
      }
    } catch (error) {
      toast('Unable to save profile changes. Please try again.', 'warn');
    } finally {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    setFirstName(currentUserData?.firstName || '');
    setMiddleName(currentUserData?.middleName || '');
    setLastName(currentUserData?.lastName || '');
    setPhone(currentUserData?.phone || '');
    setCountry(currentUserData?.country || '');
    setState(currentUserData?.state || '');
    setCity(currentUserData?.city || '');
    setZipCode(currentUserData?.zipCode || '');
    setAddress1(currentUserData?.address1 || '');
    setAddress2(currentUserData?.address2 || '');

    if (currentUserData?.country) {
      loadStates(currentUserData.country);
    } else {
      setStateList([]);
    }

    if (currentUserData?.state) {
      loadCities(currentUserData.state);
    } else {
      setCityList([]);
    }
  }, [currentUserData]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Manage Profile</div>

          <h1 className="page-title">Profile</h1>

          <p className="page-sub">
            View and manage your personal account information, contact details, and profile
            preferences. Keep your profile up to date to ensure accurate account information
            and a personalized experience across the platform.
          </p>
        </div>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">Profile</div>
      </div>
      <div className="card" style={{ padding: 25 }}>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="field-row">
            <div className="field">
              <label className="field-label">
                First Name <span className="req">*</span>
              </label>
              <input
                className="inp"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={buttonLoader}
              />
            </div>

            <div className="field">
              <label className="field-label">Middle Name</label>
              <input
                className="inp"
                placeholder="Enter middle name"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                disabled={buttonLoader}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">
                Last Name <span className="req">*</span>
              </label>
              <input
                className="inp"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={buttonLoader}
              />
            </div>

            <div className="field">
              <label className="field-label">Email</label>
              <input
                className="inp"
                value={currentUserData?.email || ''}
                disabled
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Phone</label>
              <input
                className="inp"
                placeholder="Enter phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={buttonLoader}
              />
            </div>

            <div className="field">
              <label className="field-label">Role</label>
              <input
                className="inp"
                value={currentUserData?.roleType || ''}
                disabled
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">Country</label>
              <select
                className="inp"
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  loadStates(e.target.value);
                  setState('');
                  setCity('');
                }}
                disabled={buttonLoader || !countryList?.length}
              >
                <option value="">Select Country</option>
                {countryList.map((item) => (
                  <option key={item.name} value={item.name}>{item.name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label">State</label>
              <select
                className="inp"
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  loadCities(e.target.value);
                  setCity('');
                }}
                disabled={buttonLoader || !stateList?.length}
              >
                <option value="">Select State</option>
                {stateList.map((item) => (
                  <option key={item.name} value={item.name}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label className="field-label">City</label>
              <select
                className="inp"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={buttonLoader || !cityList?.length}
              >
                <option value="">Select City</option>
                {cityList.map((item) => (
                  <option key={item.name} value={item.name}>{item.name}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label">Zip Code</label>
              <input
                className="inp"
                placeholder="Enter zip code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={buttonLoader}
              />
            </div>
          </div>

          <div className="field">
            <label className="field-label">Address Line 1</label>
            <textarea
              className="inp"
              placeholder="Enter address line 1"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              disabled={buttonLoader}
            />
          </div>

          <div className="field">
            <label className="field-label">Address Line 2</label>
            <textarea
              className="inp"
              placeholder="Enter address line 2"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              disabled={buttonLoader}
            />
          </div>

          <div className="flex between center" style={{ gap: 12, marginTop: 16 }}>
            <button
              type="submit"
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
              disabled={buttonLoader}
            >
              {buttonLoader ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserProfileSettings;